import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useT, useLang, itemHasLocale } from '../lib/i18n';
import { BACKEND_URL as API } from '../lib/config';

// ─────────────────────────────────────────────────────────────────────────
// ModernReadingList — the «Moderno» theme Reading List (s139, approved by
// Anthony: RL1 "Bookshelf"). Covers standing on a shelf line with a soft
// shadow; the pointed book lifts a little; title + author below; each book
// opens its page (/reading-list/:id). Phones: the shelf scrolls sideways.
// Header (eyebrow/title/subtitle/CTA) = CMS → Section Manager → Reading List —
// Section Configuration. No cover → brand gradient with the title.
// ─────────────────────────────────────────────────────────────────────────

const SERIF = "'Playfair Display', Georgia, serif";
const img = (src) => (src && src.startsWith('/api') ? `${API}${src}` : src);

function Cover({ book }) {
  const tt = useT();
  const [broken, setBroken] = useState(false);
  const src = img(book.image);
  return (
    <div className="relative aspect-[2/3] rounded-[2px] overflow-hidden transition-transform duration-300 group-hover:-translate-y-3 group-focus-visible:-translate-y-3"
      style={{ boxShadow: '0 22px 30px -18px rgba(20,26,58,.55)', transitionTimingFunction: 'cubic-bezier(0.23,1,0.32,1)', background: 'linear-gradient(160deg, color-mix(in srgb, var(--color-accent, #ff9c1b) 35%, #fff), var(--color-accent, #ff9c1b))' }}>
      {src && !broken
        ? <img src={src} alt={tt(book.title)} onError={() => setBroken(true)} className="w-full h-full object-cover" />
        : <span className="absolute inset-0 p-4 flex items-end font-bold text-white" style={{ fontFamily: SERIF, fontSize: '1.1rem', lineHeight: 1.2 }}>{tt(book.title)}</span>}
    </div>
  );
}

export default function ModernReadingList({ books, cmsConfig = {} }) {
  const tt = useT();
  const { lang } = useLang();
  const list = (books || []).filter(b => itemHasLocale(b.title, lang));
  if (!list.length) return null;

  const title = tt(cmsConfig.title) || 'Reading List';
  const words = title.trim().split(/\s+/);
  const last = words.length > 1 ? words.pop() : null;
  const ctaText = tt(cmsConfig.cta_text) || 'View all Books';
  const ctaUrl = tt(cmsConfig.cta_url) || '/reading-list';

  return (
    <section id="reading-list" data-testid="reading-list-section" style={{ background: 'color-mix(in srgb, var(--color-accent, #ff9c1b) 5%, #f6f5f2)' }}>
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)] py-20 lg:py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="flex flex-col gap-4 max-w-2xl">
            {tt(cmsConfig.eyebrow) && (
              <div className="inline-flex items-center gap-3 text-[11px] font-bold uppercase" style={{ letterSpacing: '.2em', color: 'var(--accent-strong, #b86500)' }}>
                <span style={{ width: 32, height: 2, background: 'var(--color-accent, #ff9c1b)' }} />{tt(cmsConfig.eyebrow)}
              </div>
            )}
            <h2 className="font-bold" style={{ fontFamily: SERIF, fontSize: 'clamp(2rem, 3.4vw, 3rem)', lineHeight: 1.08, color: 'var(--color-heading, #1a2332)' }} data-testid="reading-title">
              {words.join(' ')}{last && <> <em style={{ fontStyle: 'italic', fontWeight: 500, color: 'var(--accent-strong, #b86500)' }}>{last}</em></>}
            </h2>
            {tt(cmsConfig.subtitle) && <p className="text-base md:text-lg leading-relaxed" style={{ color: 'var(--color-body-text, #475569)' }}>{tt(cmsConfig.subtitle)}</p>}
          </div>
          <Link to={ctaUrl} className="mx-link inline-flex items-center gap-2 font-bold text-sm self-start md:self-end" style={{ color: 'var(--accent-strong, #b86500)' }} data-testid="reading-view-all">
            {ctaText} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* the shelf: covers stand on a line; scrolls sideways on phones */}
        <div className="-mx-5 sm:mx-0 overflow-x-auto sm:overflow-visible" style={{ scrollbarWidth: 'none' }}>
          <ul className="m-0 list-none flex gap-6 sm:gap-8 lg:gap-12 px-5 sm:px-0 pt-4 w-max sm:w-auto">
            {list.map(b => (
              <li key={b.id} className="w-[42vw] max-w-[190px] sm:w-[190px] flex-none">
                <Link to={`/reading-list/${b.id}`} className="group flex flex-col focus:outline-none" data-testid={`book-${b.id}`}>
                  <div className="pb-4" style={{ borderBottom: '6px solid color-mix(in srgb, var(--color-heading, #1a2332) 14%, #e8e2d8)' }}>
                    <Cover book={b} />
                  </div>
                  <div className="pt-4 flex flex-col gap-1">
                    <span className="font-bold transition-colors duration-200 group-hover:text-[color:var(--accent-strong)]" style={{ fontFamily: SERIF, fontSize: '1.1rem', lineHeight: 1.25, color: 'var(--color-heading, #1a2332)' }}>{tt(b.title)}</span>
                    {tt(b.author) && <span className="text-sm" style={{ color: 'var(--color-body-text, #64748b)' }}>{tt(b.author)}</span>}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
